import { useEffect, useRef } from 'react';

// These move their own cursor or value with the arrows, so it's left to them
const USES_ARROWS = 'textarea, select, input, [contenteditable="true"], [role="slider"]';

const DIRECTIONS = { ArrowLeft: -1, ArrowRight: 1 };

/**
 * Left and right arrows skip back and forward by `seconds`, or by `shiftSeconds` with Shift held, as in
 * any player; a held key skips once, not on every repeat. `onRelease` hears the key come back up, or the
 * window losing focus while it's down. All are read through refs so the listeners are bound once; pass a
 * null `skip` while there's nothing to skip.
 */
const useArrowsToSkip = (skip, { seconds, shiftSeconds = seconds, onRelease } = {}) => {
  const latest = useRef();
  latest.current = { skip, seconds, shiftSeconds, onRelease };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const direction = DIRECTIONS[e.key];
      // Command/Ctrl/Alt+Arrow are left to the browser and the system
      if (!direction || e.metaKey || e.ctrlKey || e.altKey) return;
      const { skip: skipBy, seconds: plain, shiftSeconds: withShift } = latest.current;
      if (e.target.closest?.(USES_ARROWS) || !skipBy) return;
      // A held key still mustn't scroll the page
      e.preventDefault();
      if (e.repeat) return;
      skipBy(direction * (e.shiftKey ? withShift : plain));
    };

    const handleKeyUp = (e) => {
      if (DIRECTIONS[e.key]) latest.current.onRelease?.();
    };

    // Its keyup never comes if the window loses focus mid-press
    const handleBlur = () => latest.current.onRelease?.();

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);
};

export default useArrowsToSkip;
